import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { Box, Card, Grid, IconButton, Switch } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import AddSparePart from "./AddSparePart";
import EditSparePart from "./EditSparePart";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface SpareParts {
  is_active: any;
  spare_part_id: number;
}

// Extracted components - OUTSIDE the main component
interface StatusSwitchCellProps {
  item: SpareParts;
  onToggleStatus: (id: number) => void;
}

const StatusSwitchCell: React.FC<StatusSwitchCellProps> = ({
  item,
  onToggleStatus,
}) => {
  return (
    <Switch
      checked={!!item.is_active}
      onChange={() => onToggleStatus(item.spare_part_id)}
    />
  );
};

interface ActionsCellProps {
  data: any;
  onEdit: (data: any) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ data, onEdit }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton aria-label="edit" onClick={() => onEdit(data)}>
        <EditIcon />
      </IconButton>
    </Box>
  );
};

// Render helper functions - OUTSIDE the component
const renderStatusSwitchCell = (
  dataIndex: number,
  rows: SpareParts[],
  handleToggleStatus: (id: number) => void
) => {
  const item = rows[dataIndex];
  return <StatusSwitchCell item={item} onToggleStatus={handleToggleStatus} />;
};

const renderActionsCell = (
  value: any,
  tableMeta: any,
  handleEditOpen: (data: any) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return <ActionsCell data={data} onEdit={handleEditOpen} />;
};

const SparePartList = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [rows, setRows] = React.useState<SpareParts[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [isEditOpen, setEditOpen] = React.useState(false);
  const [isAddOpen, setAddOpen] = React.useState(false);
  const [assetData, setAssetData] = React.useState(null);

  const handleEditOpen = (data: React.SetStateAction<null>) => {
    setAssetData(data);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleToggleStatus = async (id: number) => {
    const item = rows?.find(
      (r: { spare_part_id: number }) => r.spare_part_id === id
    );
    try {
      const response = await axios.put(`/spare-parts/${id}`, {
        is_active: item?.is_active ? 0 : 1,
      });
      fetchSuccess(response.data.message);
      fetchData();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleAddOpen = () => {
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
  };

  const columns: MUIDataTableColumn[] = [
    { name: "spare_part_name", label: "Spare Part" },
    { name: "asset_type_name", label: "Asset Type" },
    { name: "sku", label: "SKU" },
    { name: "price", label: "Price" },
    { name: "quantity", label: "Qunatity" },
    { name: "availability_status", label: "Availability" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) =>
          renderStatusSwitchCell(dataIndex, rows, handleToggleStatus),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) =>
          renderActionsCell(value, tableMeta, handleEditOpen),
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/spare-parts");
      setRows(response?.data);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <SoftButton variant="gradient" color="info" onClick={handleAddOpen}>
            Add Stock
          </SoftButton>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({
                  borders: { borderWidth, borderColor },
                }: any) => `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
          className="manage-lifts"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Assets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
      {isEditOpen && (
        <EditSparePart
          isOpen={isEditOpen}
          onClose={handleEditClose}
          assetData={assetData}
          fetchData={fetchData}
        />
      )}
      {isAddOpen && (
        <AddSparePart
          isOpen={isAddOpen}
          onClose={handleAddClose}
          fetchData={fetchData}
        />
      )}
    </DashboardLayout>
  );
};

export default SparePartList;
